import { Guesses } from "../Guesses";
import { Panel } from "./Panel";
import React from "react";
import { Drugle } from "../Drugle";
import { formatDistance } from "../../domain/geography";
import { SettingsData } from "../../hooks/useSettings";

interface InfosProps {
  isOpen: boolean;
  close: () => void;
  settingsData: SettingsData;
}

export function Infos({ isOpen, close, settingsData }: InfosProps) {
  return (
    <Panel title="How to play" isOpen={isOpen} close={close}>
      <div className="space-y-3 text-justify border-b-2 border-gray-200 pb-3 mb-3">
        <div>
          Guess the the country based on its organised crime indicators in six
          guesses.
        </div>
        <div>Each guess must be a valid country or territory.</div>
        <div>
          After each guess, you will have the distance, the direction and the
          proximity from your guess to the target location.
        </div>
      </div>
      <div className="space-y-3 text-justify border-b-2 border-gray-200 pb-3 mb-3">
        <div className="font-bold">Examples</div>
        <div>
          <Guesses
            rowCount={1}
            guesses={[
              {
                name: "Chile",
                direction: "NE",
                distance: 13_557_000,
              },
            ]}
            settingsData={settingsData}
          />
          <div className="my-2">
            Your guess <span className="uppercase font-bold">Chile</span> is{" "}
            {formatDistance(13557000, settingsData.distanceUnit)} away from the
            target country, the target country is in the North-East direction
            and you have a only 32% of proximity because it&apos;s quite far
            away!
          </div>
        </div>
        <div>
          <Guesses
            rowCount={1}
            guesses={[
              {
                name: "Finland",
                direction: "SE",
                distance: 3_206_000,
              },
            ]}
            settingsData={settingsData}
          />
          <div className="my-2">
            Your second guess{" "}
            <span className="uppercase font-bold">Finland</span> is getting
            closer! {formatDistance(3206000, settingsData.distanceUnit)} away,
            South-East direction and 84%!
          </div>
        </div>
        <div>
          <Guesses
            rowCount={1}
            guesses={[
              {
                name: "Lebanon",
                direction: "N",
                distance: 0,
              },
            ]}
            settingsData={settingsData}
          />
          <div className="my-2">
            Next guess, <span className="uppercase font-bold">Lebanon</span>,
            it&apos;s the country to guess! Congrats! 🎉
          </div>
        </div>
      </div>
      <div className="space-y-3 text-justify border-b-2 border-gray-200 pb-3 mb-3 font-bold">
        A new <Drugle /> will be available every day at 00:00 UTC!
      </div>
      <div className="space-y-3 text-justify border-b-2 border-gray-200 pb-3 mb-3">
        <div className="font-bold">About distance</div>
        <div>
          The distances displayed correspond to the distances between the
          selected and the target territory centers.
        </div>
        <div>
          For instance, the computed distance between United States and Canada
          is around {formatDistance(2_260_000, settingsData.distanceUnit)} even
          if they have a common border.
        </div>
      </div>
      <div className="space-y-3 text-justify border-b-2 border-gray-200 pb-3 mb-3">
        <div className="font-bold">About the organised crime indicators</div>
        <div>
          The organised crime indicators are based on the{" "}
          <a
            className="underline"
            href="https://ocindex.net"
            target="_blank"
            rel="noopener noreferrer"
          >
            Global Organized Crime Index
          </a>{" "}
          developed by the Global Initiative Against Transnational Organized
          Crime (GI-TOC).
        </div>
      </div>
      <div className="space-y-3 text-justify border-b-2 border-gray-200 pb-3 mb-3">
        <div className="font-bold">Credits</div>
        <div>
          <Drugle /> is based on{" "}
          <a
            className="underline"
            href="https://worldle.teuteuf.fr/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Worldle
          </a>{" "}
          created by{" "}
          <a
            className="underline"
            href="https://twitter.com/teuteuf"
            target="_blank"
            rel="noopener noreferrer"
          >
            @teuteuf
          </a>
          , which was in turn inspired by{" "}
          <a
            className="underline"
            href="https://www.powerlanguage.co.uk/wordle/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Wordle
          </a>{" "}
          created by{" "}
          <a
            className="underline"
            href="https://twitter.com/powerlanguish"
            target="_blank"
            rel="noopener noreferrer"
          >
            Josh Wardle (@powerlanguish)
          </a>
          .
        </div>
        <div>Idea by Georgia Mealings.</div>
        <div>Name by Matt Corby.</div>
        <div>
          Developed by{" "}
          <a
            className="underline"
            href="https://matthewgthomas.co.uk"
            target="_blank"
            rel="noopener noreferrer"
          >
            Matthew Gwynfryn Thomas
          </a>
          .
        </div>
      </div>
    </Panel>
  );
}
